import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { trackingConstants } from '@frontend/sports/tracking/feature';
import { DsAlert } from '@frontend/ui/alert';

import { OptionGroupTrackingService } from '../option-group-tracking.service';
import { PLAYER_STATS_IFRAME_HEIGHT, PlayerStatisticMessages, PlayerStatisticsStoreData, StatisticsInput } from './player-statistics.model';
import { PlayerStatisticsService } from './player-statistics.service';

@Component({
    selector: 'ms-player-statistics',
    styleUrl: 'player-statistics.component.scss',
    templateUrl: './player-statistics.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DsAlert],
})
export class PlayerStatisticsComponent {
    private optionGroupTrackingService = inject(OptionGroupTrackingService);
    event = input.required<EventModel>();
    marketFilter = input<string>();
    playerName = input.required<string>();
    tracking = input.required<string>();
    marketType = input<OptionGroupType>();
    isSheetViewOpen = input<boolean>();
    optionGroupName = input<string>();
    marketId = input<string>();
    optionId = input<string>();
    iFrameHeight = input<Record<string, string | undefined>>({});
    isMainMarket = input(false);

    statistics = input<PlayerStatisticsStoreData>();
    messages = input<PlayerStatisticMessages>();
    isIframeLoading = signal(true);
    safeUrl = signal<SafeResourceUrl | undefined>(undefined);
    sanitizer = inject(DomSanitizer);
    playerStatsIframeHeight = PLAYER_STATS_IFRAME_HEIGHT;
    playerStatisticsService = inject(PlayerStatisticsService);
    private isReady = computed(() => {
        const storeData = this.statistics();
        if (!storeData) {
            return false;
        }
        const { isLoading, isError, data } = storeData;
        return !isLoading && !isError && !!data && !!this.event() && !!this.playerName();
    });

    constructor() {
        effect(() => {
            const statisticsInput = {
                eventId: this.event().id,
                marketId: this.marketId(),
                playerName: this.playerName(),
                optionId: this.optionId(),
            };

            const storeData = this.statistics();
            if (!storeData) {
                return;
            }
            const { isLoading, isError, data } = storeData;
            const statisticsData = this.playerStatisticsService.getPlayerStatistics(
                data,
                this.marketId(),
                this.optionId(),
                this.isMainMarket(),
                this.marketType(),
            );

            if (!statisticsData?.isOpen) {
                return;
            }
            if (!statisticsData?.tracking) {
                this.sendTrackingEvent({
                    actionEvent: trackingConstants.EVENT_CLICK,
                    event: trackingConstants.EVENT_TRACKING,
                    eventDetailText: statisticsInput.playerName,
                });
                this.updatePlayerStatisticsStatusStore(statisticsInput, { tracking: true });
            }
            if (!isLoading && isError && !statisticsData?.loadedTracking) {
                this.sendTrackingEvent({
                    actionEvent: trackingConstants.LOAD,
                    event: trackingConstants.EVENT_CONTENT_VIEW,
                    eventDetailText: `${statisticsInput.playerName}-${trackingConstants.STATS_NOT_LOADING}`,
                });

                this.updatePlayerStatisticsStatusStore(statisticsInput, { tracking: true, loadedTracking: true });
            }

            if (this.isReady() && storeData && statisticsInput) {
                const graphUrl = this.playerStatisticsService.getPlayerStatisticsGraph(data, statisticsInput, this.marketType());
                if (graphUrl) {
                    if (!statisticsData?.loadedTracking) {
                        this.sendTrackingEvent({
                            actionEvent: trackingConstants.LOAD,
                            event: trackingConstants.EVENT_CONTENT_VIEW,
                            eventDetailText: statisticsInput.playerName,
                        });
                        this.updatePlayerStatisticsStatusStore(statisticsInput, { tracking: true, loadedTracking: true });
                    }
                    untracked(() => {
                        const safeUrl = this.safeUrl();
                        if (!safeUrl) {
                            this.safeUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(graphUrl));
                            this.isIframeLoading.set(true);
                        }
                    });
                    return;
                }

                if (!statisticsData?.loadedTracking) {
                    this.sendTrackingEvent({
                        actionEvent: trackingConstants.LOAD,
                        event: trackingConstants.EVENT_CONTENT_VIEW,
                        eventDetailText: `${statisticsInput.playerName}-${trackingConstants.STATS_NOT_LOADING}`,
                    });
                    this.updatePlayerStatisticsStatusStore(statisticsInput, { tracking: true, loadedTracking: true });
                }
            }
            untracked(() => {
                this.safeUrl.set(undefined);
                this.isIframeLoading.set(false);
            });
        });
    }

    onIframeLoad(): void {
        this.isIframeLoading.set(false);
    }

    getIframeHeight = computed(() => {
        const id = this.isMainMarket() ? this.event().id : this.optionId();
        return this.iFrameHeight()?.[id!];
    });

    private getLocationEvent(): string {
        return this.isSheetViewOpen() ? trackingConstants.SEE_ALL_OVERLAY : (this.tracking().split('/')[0] ?? '');
    }

    private sendTrackingEvent(params: { actionEvent: string; event: string; eventDetailText: string }): void {
        const event = this.event();
        this.optionGroupTrackingService.trackEvents({
            label: this.isMainMarket() ? trackingConstants.GAME_STATS : trackingConstants.PLAYER_STATS,
            actionEvent: params.actionEvent,
            participants: [],
            locationEvent: this.isMainMarket()
                ? trackingConstants.SIX_PACK
                : `${this.getLocationEvent()}${this.optionGroupName() ? ` - ${this.optionGroupName()}` : ''}`,
            eventDetailText: params.eventDetailText,
            sportId: event.sport.id,
            leagueId: event.league.id,
            event: params.event,
            marketId: this.marketId(),
            eventId: event.id,
            categoryType: trackingConstants.SCOREBOARD_EVENT,
            positionEvent: event.live ? trackingConstants.EVENT_LIVE : trackingConstants.EVENT_PRELIVE,
        });
    }

    private updatePlayerStatisticsStatusStore(
        statisticsInput: StatisticsInput,
        storeStatisticsUpdate: { tracking?: boolean; loadedTracking?: boolean },
    ): void {
        const { eventId, playerName, marketId, optionId } = statisticsInput;

        if (this.isMainMarket()) {
            this.playerStatisticsService.openTeamStatistics({
                eventId,
                mainMarketTracking: storeStatisticsUpdate.tracking,
                isMainMarketOpen: true,
                mainMarketLoadedTracking: storeStatisticsUpdate.loadedTracking,
            });
            return;
        }

        const marketFilter = this.marketFilter();
        if (marketFilter) {
            this.playerStatisticsService.openStatistics({
                eventId,
                marketFilter,
                playerName,
                marketId,
                optionId,
                marketType: this.marketType(),
                ...storeStatisticsUpdate,
            });
        }
    }
}
